import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './StartBusiness.css';

function StartBusiness() {
  // ALL HOOKS MUST BE CALLED FIRST - NO CONDITIONALS BEFORE THIS POINT
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isTestimonialTransitioning, setIsTestimonialTransitioning] = useState(false);
  const [testimonialDirection, setTestimonialDirection] = useState('next');
  const [activePortfolioSlide, setActivePortfolioSlide] = useState(0);
  const [isPortfolioTransitioning, setIsPortfolioTransitioning] = useState(false);

  // Extract lead information from URL parameters - MUST be before other useMemo hooks
  const leadInfo = useMemo(() => {
    try {
      return {
        name: searchParams.get('name') || 'there',
        email: searchParams.get('email') || '',
        phone: searchParams.get('phone') || '',
        business: searchParams.get('business') || 'your business',
        category: searchParams.get('category') || 'business'
      };
    } catch (error) {
      console.error('Error parsing search params:', error);
      return {
        name: 'there',
        email: '',
        phone: '',
        business: 'your business',
        category: 'business'
      };
    }
  }, [searchParams]);

  const firstName = useMemo(() => {
    try {
      return leadInfo.name.split(' ')[0] || 'there';
    } catch (error) {
      console.error('Error parsing firstName:', error);
      return 'there';
    }
  }, [leadInfo.name]);

  const categoryDisplay = useMemo(() => {
  const categoryMap = {
    'fashion': 'Fashion & Apparel',
    'beauty': 'Beauty & Cosmetics',
    'health': 'Health & Wellness',
    'electronics': 'Electronics & Tech',
    'home': 'Home & Living',
    'food': 'Food & Beverage',
    'sports': 'Sports & Outdoors',
    'jewelry': 'Jewelry & Accessories',
    'toys': 'Toys & Games',
      'automotive': 'Automotive',
      'construction': 'Construction',
      'general-contractor': 'General Contractor',
      'real-estate': 'Real Estate',
      'bar-restaurant': 'Bar / Restaurant',
      'ecommerce': 'E-Commerce',
      'retail': 'Retail',
      'dentist': 'Dentist',
      'doctors-health': 'Doctors & Other Health Professionals',
      'attorney-law': 'Attorney / Law Firm',
      'accountant-tax': 'Accountant / Tax Services',
      'financial-services': 'Financial Services',
      'private-equity': 'Private Equity & Investment Firms',
      'agriculture': 'Agriculture',
      'consultant': 'Consultant',
      'internet-web': 'Internet / Web-Based Services',
      'insurance': 'Insurance',
      'technology': 'Technology',
      'transportation': 'Transportation',
      'trucking': 'Trucking',
      'entertainment': 'Entertainment',
      'other': 'E-commerce'
  };
    try {
      return categoryMap[leadInfo.category?.toLowerCase()] || leadInfo.category || 'E-commerce';
    } catch (error) {
      console.error('Error parsing category:', error);
      return 'E-commerce';
    }
  }, [leadInfo.category]);

  // A/B Testing Variations
  const variations = useMemo(() => {
    try {
    const heroVariations = [
      {
        greeting: `Hi ${firstName},`,
        headline: `Ready to Take ${leadInfo.business} Online?`,
        subheadline: `Your ${categoryDisplay} store deserves a stunning Shopify presence that converts visitors into loyal customers.`
      },
      {
        greeting: `Hey ${firstName}!`,
        headline: `Let's Launch ${leadInfo.business} Together`,
        subheadline: `Transform your ${categoryDisplay} business into a 24/7 revenue-generating machine with a professionally designed Shopify store.`
      },
      {
        greeting: `Welcome, ${firstName}!`,
        headline: `${leadInfo.business} Deserves the Best`,
        subheadline: `Join hundreds of successful ${categoryDisplay} brands who've transformed their online presence with our expert Shopify solutions.`
      }
    ];

    const ctaVariations = [
      { primary: "Launch Today", secondary: "Yes, I'm Ready!" },
      { primary: "Start My Store Now", secondary: `Let's Build ${leadInfo.business}` },
      { primary: "Get Started Today", secondary: "Transform My Business" }
    ];

    const closingVariations = [
      {
        headline: `${firstName}, Your ${categoryDisplay} Empire Awaits`,
        text: `Don't let another day pass without taking ${leadInfo.business} to the next level. Let's create something extraordinary together.`
      },
      {
        headline: `Ready to Make ${leadInfo.business} Unstoppable?`,
        text: `${firstName}, the perfect time to launch your online store is now. We're here to make it happen.`
      },
      {
        headline: `${firstName}, Let's Make It Happen`,
        text: `Your ${categoryDisplay} business has incredible potential. Let us help ${leadInfo.business} reach customers worldwide.`
      }
    ];

    let varIndex = 0;
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
      const stored = sessionStorage.getItem('ab_variation');
          if (stored !== null && !isNaN(parseInt(stored, 10))) {
        varIndex = parseInt(stored, 10);
            if (varIndex < 0 || varIndex >= 3) {
              varIndex = 0;
            }
      } else {
        varIndex = Math.floor(Math.random() * 3);
        sessionStorage.setItem('ab_variation', varIndex.toString());
          }
        } else {
          varIndex = Math.floor(Math.random() * 3);
      }
    } catch (e) {
        console.warn('SessionStorage access failed:', e);
        varIndex = 0;
      }

      if (varIndex < 0 || varIndex >= heroVariations.length) {
      varIndex = 0;
    }

    return {
        hero: heroVariations[varIndex] || heroVariations[0],
        cta: ctaVariations[varIndex] || ctaVariations[0],
        closing: closingVariations[varIndex] || closingVariations[0],
      variantId: `V${varIndex + 1}`
    };
    } catch (error) {
      console.error('Error creating variations:', error);
      return {
        hero: {
          greeting: 'Hi there,',
          headline: 'Ready to Take Your Business Online?',
          subheadline: 'Your store deserves a stunning Shopify presence that converts visitors into loyal customers.'
        },
        cta: {
          primary: 'Get Started Today',
          secondary: "Yes, I'm Ready!"
        },
        closing: {
          headline: 'Let\'s Make It Happen',
          text: 'Your business has incredible potential. Let us help you reach customers worldwide.'
        },
        variantId: 'V1'
      };
    }
  }, [firstName, leadInfo.business, categoryDisplay]);

  // Data arrays - defined before useEffects that use them
  const testimonials = [
    {
      id: 1,
      quote: "They transformed my small boutique into a thriving online store. Sales increased 300% in the first three months!",
      author: "Sarah Mitchell",
      business: "Luxe Fashion Co.",
      category: "Fashion & Apparel",
      rating: 5
    },
    {
      id: 2,
      quote: "Professional, fast, and they truly understood my vision. My skincare brand finally has the online presence it deserves.",
      author: "Emma Rodriguez",
      business: "Glow Naturals",
      category: "Beauty & Cosmetics",
      rating: 5
    },
    {
      id: 3,
      quote: "From concept to launch in just 2 weeks. The team's expertise in e-commerce is unmatched. Highly recommended!",
      author: "Michael Chen",
      business: "TechGear Pro",
      category: "Electronics",
      rating: 5
    }
  ];

  const portfolioItems = [
    {
      id: 1,
      image: '/images/fashion.png',
      business: 'Luxe Fashion Co.',
      category: 'fashion',
      result: '300% increase in sales',
      description: 'Premium fashion boutique with elegant design'
    },
    {
      id: 19,
      image: '/images/fashion1.png',
      business: 'Elite Fashion House',
      category: 'fashion',
      result: '380% sales growth',
      description: 'High-end fashion store with sophisticated design'
    },
    {
      id: 18,
      image: '/images/beauty.jpg',
      business: 'Glamour Beauty Co.',
      category: 'beauty',
      result: '350% revenue growth',
      description: 'Premium beauty and cosmetics store with elegant design'
    },
    {
      id: 2,
      image: '/images/health.png',
      business: 'FitLife Wellness',
      category: 'health',
      result: '250% growth in 6 months',
      description: 'Health & wellness store with modern UI'
    },
    {
      id: 17,
      image: '/images/health1.png',
      business: 'Vital Health Solutions',
      category: 'health',
      result: '300% sales increase',
      description: 'Premium health and wellness store with clean design'
    },
    {
      id: 3,
      image: '/images/tech.png',
      business: 'TechGear Pro',
      category: 'electronics',
      result: '400% conversion boost',
      description: 'Electronics store with cutting-edge design'
    },
    {
      id: 16,
      image: '/images/tech1.png',
      business: 'Digital Tech Solutions',
      category: 'electronics',
      result: '350% revenue increase',
      description: 'Modern electronics store with futuristic design'
    },
    {
      id: 4,
      image: '/images/HomeDecor.png',
      business: 'Modern Home Living',
      category: 'home',
      result: '200% revenue increase',
      description: 'Home decor store with beautiful aesthetics'
    },
    {
      id: 15,
      image: '/images/HomeDecor1.png',
      business: 'Elegant Home Interiors',
      category: 'home',
      result: '280% sales growth',
      description: 'Premium home decor store with sophisticated design'
    },
    {
      id: 5,
      image: '/images/coffee.png',
      business: 'Artisan Coffee Co.',
      category: 'food',
      result: '180% sales growth',
      description: 'Coffee brand with warm, inviting design'
    },
    {
      id: 10,
      image: '/images/food-&-beverage.jpg',
      business: 'Gourmet Delights',
      category: 'food',
      result: '290% revenue increase',
      description: 'Food & beverage store with elegant culinary design'
    },
    {
      id: 6,
      image: '/images/FitLife.png',
      business: 'FitLife Active',
      category: 'sports',
      result: '320% traffic increase',
      description: 'Sports & fitness store with dynamic layout'
    },
    {
      id: 11,
      image: '/images/sports.jpg',
      business: 'Athletic Pro Shop',
      category: 'sports',
      result: '280% sales growth',
      description: 'Premium sports equipment store with modern design'
    },
    {
      id: 7,
      image: '/images/automotive.jpg',
      business: 'AutoMax Motors',
      category: 'automotive',
      result: '280% sales increase',
      description: 'Automotive store with sleek, modern design'
    },
    {
      id: 14,
      image: '/images/Automotive1.jpg',
      business: 'Premium Auto Parts',
      category: 'automotive',
      result: '320% revenue growth',
      description: 'Premium automotive parts store with professional design'
    },
    {
      id: 8,
      image: '/images/construction.jpg',
      business: 'BuildPro Construction',
      category: 'construction',
      result: '350% revenue growth',
      description: 'Construction company store with professional design'
    },
    {
      id: 9,
      image: '/images/construction1.jpg',
      business: 'Elite Builders Co.',
      category: 'construction',
      result: '320% sales increase',
      description: 'Premium construction services with modern e-commerce platform'
    },
    {
      id: 12,
      image: '/images/Jewelry.jpg',
      business: 'Luxury Gems Collection',
      category: 'jewelry',
      result: '400% revenue increase',
      description: 'Premium jewelry store with elegant, luxurious design'
    },
    {
      id: 13,
      image: '/images/toy.jpg',
      business: 'Playtime Paradise',
      category: 'toys',
      result: '250% sales growth',
      description: 'Fun and vibrant toys store with playful design'
    },
    {
      id: 20,
      image: '/images/real-estate.jpg',
      business: 'Premium Properties Group',
      category: 'real-estate',
      result: '450% revenue increase',
      description: 'Professional real estate services with elegant design'
    },
    {
      id: 21,
      image: '/images/bar.jpg',
      business: 'Elite Bar & Lounge',
      category: 'bar-restaurant',
      result: '320% sales growth',
      description: 'Premium bar and restaurant with sophisticated design'
    },
    {
      id: 22,
      image: '/images/e-commerce.jpg',
      business: 'Digital Commerce Hub',
      category: 'ecommerce',
      result: '400% revenue increase',
      description: 'Modern e-commerce platform with versatile design'
    },
    {
      id: 23,
      image: '/images/retail.jpg',
      business: 'Premium Retail Store',
      category: 'retail',
      result: '350% sales growth',
      description: 'Modern physical retail storefront with sophisticated design'
    },
    {
      id: 24,
      image: '/images/dentist.jpg',
      business: 'Elite Dental Care',
      category: 'dentist',
      result: '380% patient growth',
      description: 'Professional dental services with clean, modern design'
    },
    {
      id: 25,
      image: '/images/agriculture.jpg',
      business: 'Green Fields Farm Supply',
      category: 'agriculture',
      result: '300% sales increase',
      description: 'Modern agriculture store with natural, organic design'
    },
    {
      id: 26,
      image: '/images/consultant.jpg',
      business: 'Strategic Business Solutions',
      category: 'consultant',
      result: '420% client growth',
      description: 'Professional consulting services with modern design'
    },
    {
      id: 27,
      image: '/images/internet.jpg',
      business: 'Digital Web Solutions',
      category: 'internet-web',
      result: '450% revenue increase',
      description: 'Modern internet and web-based services with futuristic design'
    },
    {
      id: 28,
      image: '/images/insurance.jpg',
      business: 'Secure Coverage Insurance',
      category: 'insurance',
      result: '380% policy growth',
      description: 'Professional insurance services with trustworthy design'
    },
    {
      id: 29,
      image: '/images/technology.jpg',
      business: 'Innovation Tech Solutions',
      category: 'technology',
      result: '500% revenue growth',
      description: 'Cutting-edge technology services with futuristic design'
    },
    {
      id: 30,
      image: '/images/transportation.jpg',
      business: 'Premium Ride Services',
      category: 'transportation',
      result: '450% bookings increase',
      description: 'Modern private car booking and ride services with sleek design'
    },
    {
      id: 31,
      image: '/images/general.jpg',
      business: 'Home Maintenance Experts',
      category: 'general-contractor',
      result: '400% service bookings',
      description: 'Professional plumbing, cleaning, and home maintenance services'
    },
    {
      id: 32,
      image: '/images/doctor.jpg',
      business: 'Medical Excellence Clinic',
      category: 'doctors-health',
      result: '350% patient growth',
      description: 'Professional healthcare services with modern medical practice design'
    },
    {
      id: 33,
      image: '/images/law-firm.jpg',
      business: 'Legal Excellence Partners',
      category: 'attorney-law',
      result: '380% client growth',
      description: 'Professional legal services with sophisticated law firm design'
    },
    {
      id: 34,
      image: '/images/Accounting.jpg',
      business: 'Financial Excellence Group',
      category: 'accountant-tax',
      result: '420% client growth',
      description: 'Professional accounting and tax services with modern financial design'
    },
    {
      id: 35,
      image: '/images/entertainment.jpg',
      business: 'Entertainment Hub',
      category: 'entertainment',
      result: '500% engagement growth',
      description: 'Dynamic entertainment services with vibrant and engaging design'
    },
    {
      id: 36,
      image: '/images/trucking.jpg',
      business: 'Logistics Pro Transport',
      category: 'trucking',
      result: '450% fleet growth',
      description: 'Professional trucking and logistics services with modern fleet management design'
    },
    {
      id: 37,
      image: '/images/Financial.jpg',
      business: 'Wealth Management Solutions',
      category: 'financial-services',
      result: '480% client growth',
      description: 'Comprehensive financial services with sophisticated wealth management design'
    },
    {
      id: 38,
      image: '/images/Private-Equity.jpg',
      business: 'Capital Growth Partners',
      category: 'private-equity',
      result: '520% portfolio growth',
      description: 'Premium private equity and investment services with sophisticated financial design'
    }
  ];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Create geometric shapes and particles for CTA section
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const createShapes = () => {
      const background = document.getElementById('cta-geometric-background');
      if (!background) return;

      // Clear existing shapes
      background.innerHTML = '';
      
      const shapeTypes = ['cta-shape-square', 'cta-shape-circle', 'cta-shape-triangle', 'cta-shape-rectangle'];
      
      for (let i = 0; i < 40; i++) {
        const shape = document.createElement('div');
        const shapeClass = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
        shape.className = `cta-shape ${shapeClass}`;
        
        // Random positions
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        
        // Random animation properties
        const delay = Math.random() * 10;
        const duration = Math.random() * 10 + 10;
        
        // Apply styles
        shape.style.left = `${posX}%`;
        shape.style.top = `${posY}%`;
        shape.style.animationDelay = `${delay}s`;
        shape.style.animationDuration = `${duration}s`;
        
        background.appendChild(shape);
      }
    };

    const createParticles = () => {
      const particlesContainer = document.getElementById('cta-particles');
      if (!particlesContainer) return;

      // Clear existing particles
      particlesContainer.innerHTML = '';
      
      for (let i = 0; i < 100; i++) {
        const particle = document.createElement('div');
        particle.className = 'cta-particle';
        
        // Random positions
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        
        // Random animation properties
        const delay = Math.random() * 8;
        const duration = Math.random() * 4 + 4;
        
        // Apply styles
        particle.style.left = `${posX}%`;
        particle.style.top = `${posY}%`;
        particle.style.animationDelay = `${delay}s`;
        particle.style.animationDuration = `${duration}s`;
        
        particlesContainer.appendChild(particle);
      }
    };

    // Mouse movement interaction
    const addMouseInteraction = () => {
      const handleMouseMove = (e) => {
        const shapes = document.querySelectorAll('.cta-shape');
        if (shapes.length === 0) return;
        
        const x = e.clientX / window.innerWidth;
        const y = e.clientY / window.innerHeight;
        
        shapes.forEach(shape => {
          const speed = 0.05;
          const currentLeft = parseFloat(shape.style.left) || 0;
          const currentTop = parseFloat(shape.style.top) || 0;
          
          shape.style.left = `${currentLeft + (x - 0.5) * speed}%`;
          shape.style.top = `${currentTop + (y - 0.5) * speed}%`;
        });
      };

      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      return () => window.removeEventListener('mousemove', handleMouseMove);
    };

    // Initialize with a small delay to ensure DOM is ready
    let cleanupMouse;
    const timer = setTimeout(() => {
      createShapes();
      createParticles();
      cleanupMouse = addMouseInteraction();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanupMouse) cleanupMouse();
    };
  }, []);

  // Data
  const services = [
    {
      id: 1,
      title: 'Shopify Design & Development',
      description: 'Beautiful, conversion-focused Shopify stores built from scratch with custom themes, seamless integrations, and complete store configuration.',
      highlight: 'Ready to sell in days'
    },
    {
      id: 2,
      title: 'Migration to Shopify',
      description: 'Seamless migration from any platform with complete data transfer, URL redirects, and zero downtime to keep your business running.',
      highlight: 'Hassle-free transition'
    },
    {
      id: 3,
      title: 'Support & Maintenance',
      description: 'Ongoing technical support, regular updates, performance optimization, and proactive monitoring to keep your store running smoothly.',
      highlight: '24/7 assistance'
    }
  ];

  const stats = [
    { id: 1, number: '500+', label: 'Stores Launched' },
    { id: 2, number: '98%', label: 'Client Satisfaction' },
    { id: 3, number: '2 Weeks', label: 'Average Launch Time' },
    { id: 4, number: '24/7', label: 'Support Available' }
  ];

  const benefits = [
    { id: 1, icon: '⚡', text: 'Lightning-fast delivery' },
    { id: 2, icon: '🎨', text: 'Unlimited design revisions' },
    { id: 3, icon: '📱', text: 'Mobile-first responsive' },
    { id: 4, icon: '🔒', text: 'Secure & reliable' },
    { id: 5, icon: '📈', text: 'Conversion optimized' },
    { id: 6, icon: '🤝', text: 'Dedicated support' }
  ];

  const categoryMap = {
    'fashion': 'Fashion & Apparel',
    'beauty': 'Beauty & Cosmetics',
    'health': 'Health & Wellness',
    'electronics': 'Electronics & Tech',
    'home': 'Home & Living',
    'food': 'Food & Beverage',
    'sports': 'Sports & Outdoors',
    'jewelry': 'Jewelry & Accessories',
    'toys': 'Toys & Games',
    'automotive': 'Automotive',
    'construction': 'Construction',
    'real-estate': 'Real Estate',
    'other': 'E-commerce'
  };

  // Testimonial navigation functions
  const nextTestimonial = () => {
    if (isTestimonialTransitioning) return;
    setTestimonialDirection('next');
    setIsTestimonialTransitioning(true);
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsTestimonialTransitioning(false), 600);
  };

  const prevTestimonial = () => {
    if (isTestimonialTransitioning) return;
    setTestimonialDirection('prev');
    setIsTestimonialTransitioning(true);
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsTestimonialTransitioning(false), 600);
  };

  const goToTestimonial = (index) => {
    if (isTestimonialTransitioning || index === activeTestimonial) return;
    setTestimonialDirection(index > activeTestimonial ? 'next' : 'prev');
    setIsTestimonialTransitioning(true);
    setActiveTestimonial(index);
    setTimeout(() => setIsTestimonialTransitioning(false), 600);
  };

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTestimonialTransitioning) {
        nextTestimonial();
      }
    }, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestimonialTransitioning]);

  // Filter portfolio items by category or show all for "other"
  // Note: Automotive and Construction items are category-specific only and excluded from "all items" view
  const displayPortfolio = useMemo(() => {
    const category = leadInfo.category?.toLowerCase();
    if (!category || category === 'other' || category === 'business') {
      // Show all items EXCEPT category-specific ones (automotive, construction)
      return portfolioItems.filter(item => item.category !== 'automotive' && item.category !== 'construction');
    }
    // Show only items matching the selected category
    return portfolioItems.filter(item => item.category === category);
  }, [leadInfo.category]);

  // Portfolio slider navigation functions
  const nextPortfolioSlide = () => {
    if (isPortfolioTransitioning || displayPortfolio.length === 0) return;
    setIsPortfolioTransitioning(true);
    setActivePortfolioSlide((prev) => (prev + 1) % displayPortfolio.length);
    setTimeout(() => setIsPortfolioTransitioning(false), 600);
  };

  const prevPortfolioSlide = () => {
    if (isPortfolioTransitioning || displayPortfolio.length === 0) return;
    setIsPortfolioTransitioning(true);
    setActivePortfolioSlide((prev) => (prev - 1 + displayPortfolio.length) % displayPortfolio.length);
    setTimeout(() => setIsPortfolioTransitioning(false), 600);
  };

  const goToPortfolioSlide = (index) => {
    if (isPortfolioTransitioning || index === activePortfolioSlide || displayPortfolio.length === 0) return;
    setIsPortfolioTransitioning(true);
    setActivePortfolioSlide(index);
    setTimeout(() => setIsPortfolioTransitioning(false), 600);
  };

  // Reset active slide when portfolio changes
  useEffect(() => {
    setActivePortfolioSlide(0);
  }, [displayPortfolio.length]);

  // Auto-rotate portfolio slider (only if more than 1 item)
  useEffect(() => {
    if (displayPortfolio.length <= 1) return;
    const interval = setInterval(() => {
      if (!isPortfolioTransitioning && displayPortfolio.length > 1) {
        setActivePortfolioSlide((prev) => (prev + 1) % displayPortfolio.length);
        setIsPortfolioTransitioning(true);
        setTimeout(() => setIsPortfolioTransitioning(false), 600);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isPortfolioTransitioning, displayPortfolio.length]);

  // Handle CTA click - Navigate to get-started form with pre-filled data
  const handleCTAClick = () => {
    const params = new URLSearchParams();
    if (leadInfo.name && leadInfo.name !== 'there') params.set('name', leadInfo.name);
    if (leadInfo.email) params.set('email', leadInfo.email);
    if (leadInfo.phone) params.set('phone', leadInfo.phone);
    if (leadInfo.business && leadInfo.business !== 'your business') params.set('business', leadInfo.business);
    if (leadInfo.category && leadInfo.category !== 'business') params.set('category', leadInfo.category);

    const queryString = params.toString();
    navigate(`/get-started${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <section className="start-business-section">
      {/* Background */}
      <div className="start-business-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <div className="container">
        {/* Hero Section */}
        <header className="hero-section">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            <span>Trusted by 500+ {categoryDisplay} Brands</span>
          </div>

          <p className="hero-greeting">{variations.hero.greeting}</p>
          <h1 className="hero-title">
            <span className="title-line">{variations.hero.headline}</span>
          </h1>
          <p className="hero-subtitle">{variations.hero.subheadline}</p>

          <div className="hero-cta-group">
            <button className="btn-primary btn-large btn-glow" onClick={handleCTAClick}>
              <span className="btn-text">{variations.cta.primary}</span>
                <span className="btn-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
            </button>
            <p className="hero-cta-note">Free consultation • No commitment required</p>
          </div>

          {/* Stats */}
          <div className="stats-bar">
            {stats.map((stat) => (
              <div key={stat.id} className="stat-item">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </header>

        {/* Services Section */}
        <section className="services-section">
          <div className="section-header">
            <span className="section-badge">Our Services</span>
            <h2 className="section-title">
              Everything You Need to <span className="highlight">Succeed</span>
            </h2>
            <p className="section-subtitle">From design to launch, we handle every aspect of your store</p>
          </div>

          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 48 48" fill="none">
                    <rect x="6" y="6" width="36" height="36" rx="4" stroke="currentColor" strokeWidth="2.5"/>
                    <path d="M6 18H42" stroke="currentColor" strokeWidth="2.5"/>
                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                    <circle cx="18" cy="12" r="2" fill="currentColor"/>
                    <rect x="12" y="24" width="24" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <span className="service-highlight">{service.highlight}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Portfolio Section */}
        <section className="portfolio-section">
          <div className="section-header fade-in">
            <span className="section-badge">Our Work</span>
            <h2 className="section-title">
              {(() => {
                const category = leadInfo.category?.toLowerCase();
                const isAllItems = !category || category === 'other' || category === 'business';
                return isAllItems ? (
                  <>See What We've <span className="highlight">Built</span></>
                ) : (
                  <>Success Stories in <span className="highlight">{categoryDisplay}</span></>
                );
              })()}
            </h2>
            <p className="section-subtitle">
              {(() => {
                const category = leadInfo.category?.toLowerCase();
                const isAllItems = !category || category === 'other' || category === 'business';
                return isAllItems
                  ? 'Explore our portfolio of successful Shopify stores across various industries'
                  : `Real results from ${categoryDisplay} brands we've helped succeed`;
              })()}
            </p>
          </div>

          {/* Portfolio Slider - Always show slider format */}
          <div className="portfolio-slider fade-in">
            <div className="portfolio-slider-background">
              <div className="portfolio-slider-glow"></div>
            </div>

            <div className="portfolio-slider-container">
              <div
                className={`portfolio-slider-track ${isPortfolioTransitioning ? 'transitioning' : ''}`}
                style={{ transform: `translateX(-${activePortfolioSlide * 100}%)` }}
              >
                {displayPortfolio.map((item, index) => (
                  <div
                    key={item.id}
                    className={`portfolio-slide ${index === activePortfolioSlide ? 'active' : ''}`}
                  >
                    <div className="portfolio-card">
                      <div className="portfolio-image-wrapper">
                        <img 
                          src={item.image} 
                          alt={item.business}
                          className="portfolio-image"
                          loading="lazy"
                        />
                        <div className="portfolio-overlay">
                          <div className="portfolio-result">{item.result}</div>
                        </div>
                      </div>
                      <div className="portfolio-info">
                        <h3 className="portfolio-business">{item.business}</h3>
                        <p className="portfolio-description">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {displayPortfolio.length > 1 && (
              <div className="portfolio-slider-controls">
                <button
                  className={`portfolio-slider-btn prev ${isPortfolioTransitioning ? 'disabled' : ''}`}
                  onClick={prevPortfolioSlide}
                  disabled={isPortfolioTransitioning}
                  aria-label="Previous portfolio item"
                >
                  <span className="btn-bg"></span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                <div className="portfolio-slider-indicators">
                  {displayPortfolio.map((_, index) => (
                    <button
                      key={index}
                      className={`portfolio-indicator ${index === activePortfolioSlide ? 'active' : ''}`}
                      onClick={() => goToPortfolioSlide(index)}
                      disabled={isPortfolioTransitioning}
                      aria-label={`Go to portfolio item ${index + 1}`}
                    >
                      <span className="indicator-fill"></span>
                    </button>
                  ))}
                </div>

                <button
                  className={`portfolio-slider-btn next ${isPortfolioTransitioning ? 'disabled' : ''}`}
                  onClick={nextPortfolioSlide}
                  disabled={isPortfolioTransitioning}
                  aria-label="Next portfolio item"
                >
                  <span className="btn-bg"></span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits-section">
          <div className="benefits-content">
            <div className="benefits-text">
              <span className="section-badge">Why Choose Us</span>
              <h2 className="section-title">Built for Success, <span className="highlight">Designed for Growth</span></h2>
              <p className="section-subtitle">We don't just build stores, we create revenue-generating machines that help brands thrive.</p>
              <div className="benefits-grid">
                {benefits.map((benefit) => (
                  <div key={benefit.id} className="benefit-item">
                    <span className="benefit-icon">{benefit.icon}</span>
                    <span className="benefit-text">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="benefits-visual">
              <div className="visual-card">
                <div className="visual-header">
                  <div className="visual-dots"><span></span><span></span><span></span></div>
                  <span className="visual-title">{leadInfo.business}</span>
                </div>
                <div className="visual-content">
                  <div className="visual-mockup">
                    <div className="mockup-header"></div>
                    <div className="mockup-hero"></div>
                    <div className="mockup-products">
                      <div className="mockup-product"></div>
                      <div className="mockup-product"></div>
                      <div className="mockup-product"></div>
                    </div>
                  </div>
                </div>
                <div className="visual-badge">Your Future Store</div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section">
          <div className="section-header fade-in">
            <span className="section-badge">Success Stories</span>
            <h2 className="section-title">Join Hundreds of<br /><span className="highlight">Happy Clients</span></h2>
          </div>

          <div className="testimonials-carousel fade-in">
            <div className="carousel-background">
              <div className="carousel-glow"></div>
              <div className="floating-quotes">
                <span className="quote-mark quote-left">"</span>
                <span className="quote-mark quote-right">"</span>
              </div>
            </div>

            <div className="carousel-container">
              <div
                className={`carousel-track ${isTestimonialTransitioning ? `transitioning-${testimonialDirection}` : ''}`}
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className={`testimonial-slide ${index === activeTestimonial ? 'active' : ''}`}
                  >
                    <div className="testimonial-card">
                      <div className="card-shimmer"></div>

                      <div className="quote-icon-top">
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                          <path d="M12 24C12 20.6863 14.6863 18 18 18V18C18 14.6863 15.3137 12 12 12V12C8.68629 12 6 14.6863 6 18V24C6 27.3137 8.68629 30 12 30V30V24Z" fill="currentColor" opacity="0.15"/>
                          <path d="M36 24C36 20.6863 38.6863 18 42 18V18C42 14.6863 39.3137 12 36 12V12C32.6863 12 30 14.6863 30 18V24C30 27.3137 32.6863 30 36 30V30V24Z" fill="currentColor" opacity="0.15"/>
                        </svg>
                      </div>

                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                          <svg
                            key={i}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="star"
                            style={{ animationDelay: `${i * 0.1}s` }}
                          >
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                      </svg>
                    ))}
                  </div>

                      <p className="testimonial-quote">
                        <span className="quote-text">{testimonial.quote}</span>
                      </p>

                  <div className="testimonial-author">
                        <div className="author-avatar-wrapper">
                          <div className="avatar-ring"></div>
                    <div className="author-avatar">{testimonial.author.split(' ').map(n => n[0]).join('')}</div>
                        </div>
                    <div className="author-info">
                          <div className="author-name">{testimonial.author}</div>
                          <div className="author-business">{testimonial.business}</div>
                          <div className="author-category">{testimonial.category}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
            </div>

            <div className="carousel-controls">
              <button
                className={`carousel-btn prev ${isTestimonialTransitioning ? 'disabled' : ''}`}
                onClick={prevTestimonial}
                disabled={isTestimonialTransitioning}
                aria-label="Previous testimonial"
              >
                <span className="btn-bg"></span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="carousel-indicators">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                    className={`indicator ${index === activeTestimonial ? 'active' : ''}`}
                    onClick={() => goToTestimonial(index)}
                    disabled={isTestimonialTransitioning}
                  aria-label={`Go to testimonial ${index + 1}`}
                  >
                    <span className="indicator-fill"></span>
                  </button>
                ))}
              </div>

              <button
                className={`carousel-btn next ${isTestimonialTransitioning ? 'disabled' : ''}`}
                onClick={nextTestimonial}
                disabled={isTestimonialTransitioning}
                aria-label="Next testimonial"
              >
                <span className="btn-bg"></span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Final CTA Section - Full Width */}
        <section className="final-cta-section">
        <div className="cta-background-wrapper">
          <div className="cta-geometric-background" id="cta-geometric-background"></div>
          <div className="cta-particles" id="cta-particles"></div>
          <div className="cta-gradient-overlay"></div>
        </div>
        <div className="cta-content-wrapper">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">{variations.closing.headline}</h2>
              <p className="cta-text">{variations.closing.text}</p>
              <button className="btn-primary btn-large btn-glow" onClick={handleCTAClick}>
                <span className="btn-text">{variations.cta.secondary}</span>
                  <span className="btn-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
              </button>
              <div className="cta-trust">
                <div className="trust-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1.33334L2 4.00001V7.33334C2 10.7333 4.6 13.9333 8 14.6667C11.4 13.9333 14 10.7333 14 7.33334V4.00001L8 1.33334Z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M6 8L7.33333 9.33334L10 6.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>100% Satisfaction Guaranteed</span>
                </div>
                <div className="trust-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6.67" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M8 4V8L10.67 9.33" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>Response within 24 hours</span>
                </div>
                </div>
              </div>
            </div>
          </div>
        </section>
    </section>
  );
}

export default StartBusiness;
